import {
  Alert,
  Button,
  Chip,
  Divider,
  Input,
  Sheet,
  Typography,
} from "@mui/joy";
import { Box, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Contractor_Entity } from "../../entities/contractor";
import {
  formOpenSelector,
  selectedContractorIDsSelector,
  setFormOpen,
  setSelectedContractorIDs,
  setTmpGig,
} from "../../reducers/rhapsody";
import {
  useCreateContractorMutation,
  useDeleteContractorMutation,
  useUpdateContractorMutation,
} from "../../redux/contractor/contractorEndpoints";
import { useContractor } from "../../redux/contractor/contractorHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";
import DialogClose from "../dialogClose/dialogClose";
import { GooglePlaceSuggestions } from "../googlePlaceSuggestions/googlePlaceSuggestions";
import { usePro } from "../usePro/usePro";
import { NerdPhoneField } from "@nerdjs/nerd-ui";
import Upload from "../fileUpload/fileUpload";
import {
  DialogHeader,
  FieldLabel,
  GroupCaption,
  dialogPaperSx,
  groupSheetSx,
  pillButtonSx,
  tileColors,
} from "../formKit/formKit";

/**
 *
 * @returns {ReactElement} FormContractor page
 */
export function FormContractor() {
  const contractorIDs = useSelector(selectedContractorIDsSelector);
  const { contractor } = useContractor(contractorIDs);
  const open = useSelector(formOpenSelector("contractor"));
  const gigFormOpen = useSelector(formOpenSelector("gig"));
  const [_contractor, setContractor] = useState<Partial<Contractor_Entity>>({});
  const pro = usePro();
  const [createContractor] = useCreateContractorMutation();
  const [updateContractor] = useUpdateContractorMutation();
  const [deleteContractor] = useDeleteContractorMutation();

  const askQuestion = useAskQuestion();

  const dispatch = useDispatch();

  useEffect(() => {
    if (contractor?.id) {
      setContractor(contractor);
    }
  }, [contractor]);

  const onClose = () => {
    setContractor({});
    dispatch(setSelectedContractorIDs());
    dispatch(setFormOpen({ isOpen: false, formID: "contractor" }));
  };

  const save = async () => {
    if (!pro)
      return dispatch(setFormOpen({ formID: "upgradeOpen", isOpen: true }));
    if (contractorIDs && _contractor) {
      updateContractor({ id: contractorIDs.id, body: _contractor });
    } else if (_contractor) {
      const promise = createContractor(_contractor);
      // Quick-add from the Service form: select the new organization straight
      // into the open gig draft (the list query refreshes it into the dropdown).
      if (gigFormOpen) {
        try {
          const created = await promise.unwrap();
          if (created?.id) dispatch(setTmpGig({ contractorID: created.id }));
        } catch (e) {
          console.error(e);
        }
      }
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: <Typography>You are about to delete a Contractor.</Typography>,
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (contractorIDs?.id) deleteContractor(contractorIDs?.id);
    onClose();
  };

  return (
    <DialogClose
      open={open}
      onClose={onClose}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle>
        <DialogHeader
          title={`${contractorIDs?.id ? "Edit" : "Create"} Organization`}
          subtitle={
            !contractorIDs?.id
              ? "Keep track of who hires you and how to reach them."
              : undefined
          }
        />
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: 500,
          maxWidth: "100%",
        }}
      >
        {!pro ? (
          <Alert
            color="info"
            startDecorator={<i className="fa-regular fa-circle-info"></i>}
          >
            Add Organizations you play outside of Rhapsody allowing you to
            manage all your work seamlessly in one platform.
          </Alert>
        ) : (
          []
        )}
        <GroupCaption optional>Cover artwork</GroupCaption>
        <Sheet variant="soft" sx={{ ...groupSheetSx, p: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {_contractor.coverURL ? (
              <img
                src={_contractor.coverURL}
                style={{ width: 95, height: 95, borderRadius: "8px" }}
              />
            ) : (
              <Upload
                onUpload={(f) => setContractor((t) => ({ ...t, coverURL: f }))}
                cropDimension={{ width: 250, height: 250 }}
              >
                <Box
                  sx={{
                    width: 95,
                    height: 95,
                    flexShrink: 0,
                    border: "dashed 1.5px #9e9e9e",
                    borderRadius: "8px",
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography level="h4" sx={{ color: "#9e9e9e" }}>
                    <i className="fa-duotone fa-image"></i>
                  </Typography>
                </Box>
              </Upload>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                level="body2"
                sx={{ color: "black", fontWeight: 600 }}
              >
                Organization Cover
              </Typography>
              <Typography level="body3">
                An artwork respresenting the Organization. It must be at least
                250px by 250px with a max size of 512KB. The supported MIME
                types are JPG, PNG, and GIf.
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Upload
                  cropDimension={{ width: 250, height: 250 }}
                  onUpload={(f) =>
                    setContractor((t) => ({ ...t, coverURL: f }))
                  }
                />
                {_contractor.coverURL ? (
                  <Chip
                    onClick={() =>
                      setContractor((t) => ({ ...t, coverURL: "" }))
                    }
                    size="sm"
                    variant="outlined"
                    color="danger"
                  >
                    Remove Cover
                  </Chip>
                ) : (
                  []
                )}
              </Box>
            </Box>
          </Box>
        </Sheet>
        <GroupCaption>The basics</GroupCaption>
        <Sheet variant="soft" sx={groupSheetSx}>
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            autoFocus
            placeholder="e.g. City Symphony Orchestra"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-signature"
                color={tileColors.blue}
                label="Name"
              />
            }
            value={_contractor.name}
            onChange={(e) =>
              setContractor((v) => ({ ...v, name: e.target.value }))
            }
            type="text"
          />
          <Divider />
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-align-left"
                color={tileColors.indigo}
                label="Description"
              />
            }
            value={_contractor.description}
            onChange={(e) =>
              setContractor((v) => ({ ...v, description: e.target.value }))
            }
            type="text"
          />
        </Sheet>
        <GroupCaption>How to reach them</GroupCaption>
        <Sheet variant="soft" sx={groupSheetSx}>
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-user"
                color={tileColors.purple}
                label="Contact"
              />
            }
            value={_contractor.contact}
            onChange={(e) =>
              setContractor((v) => ({ ...v, contact: e.target.value }))
            }
            type="text"
          />
          <Divider />
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-envelope"
                color={tileColors.blue}
                label="Email"
              />
            }
            value={_contractor.email}
            onChange={(e) =>
              setContractor((v) => ({ ...v, email: e.target.value }))
            }
            type="text"
          />
          <Divider />
          <Box
            sx={{
              display: "flex",
              pl: 1.5,
              pr: 1,
              alignItems: "center",
              height: 40,
              justifyContent: "space-between",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <FieldLabel
              icon="fa-solid fa-phone"
              color={tileColors.green}
              label="Phone"
            />
            <Box
              sx={{
                width: 200,
                height: 40,
                position: "absolute",
                top: 4,
                right: 0,
              }}
              className="musician"
            >
              <NerdPhoneField
                value={_contractor.phone ?? ""}
                onAccept={(e) => {
                  setContractor((v) => ({ ...v, phone: e }));
                }}
                textFieldProps={{
                  fullWidth: true,
                  variant: "standard",
                  inputProps: {
                    border: "none",
                    disableUnderline: true,
                    style: { textAlign: "right" },
                  },
                  size: "small",
                  sx: { background: "transparent" },
                }}
              />
            </Box>
          </Box>
        </Sheet>
        <GroupCaption optional>Address</GroupCaption>
        <GooglePlaceSuggestions
          onSelect={(e) => {
            setContractor((v) => ({
              ...v,
              street: e.address ? e.address : "",
              city: e.city ? e.city : "",
              state: e.state ? e.state : "",
              zipcode: e.zipcode ? e.zipcode : "",
              tzName: e.tzName ? e.tzName : "",
              longitude: e.longitude,
              latitude: e.latitude,
            }));
          }}
        />
        <Sheet variant="soft" sx={groupSheetSx}>
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-location-dot"
                color={tileColors.orange}
                label="Street"
              />
            }
            value={_contractor.street}
            onChange={(e) =>
              setContractor((v) => ({ ...v, address: e.target.value }))
            }
            type="text"
          />
          <Divider />
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-city"
                color={tileColors.blue}
                label="City"
              />
            }
            value={_contractor.city}
            onChange={(e) =>
              setContractor((v) => ({ ...v, city: e.target.value }))
            }
            type="text"
          />
          <Divider />
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-hashtag"
                color={tileColors.gray}
                label="Zipcode"
              />
            }
            value={_contractor.zipcode}
            onChange={(e) =>
              setContractor((v) => ({ ...v, zipcode: e.target.value }))
            }
            type="text"
          />
          <Divider />
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-map"
                color={tileColors.green}
                label="State"
              />
            }
            value={_contractor.state}
            onChange={(e) =>
              setContractor((v) => ({ ...v, state: e.target.value }))
            }
            type="text"
          />
          <Divider />
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-globe"
                color={tileColors.teal}
                label="Country"
              />
            }
            value={_contractor.country}
            onChange={(e) =>
              setContractor((v) => ({ ...v, country: e.target.value }))
            }
            type="text"
          />
        </Sheet>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {contractorIDs?.id ? (
          <Button
            variant="soft"
            onClick={askDelete}
            color="danger"
            sx={pillButtonSx}
          >
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={onClose}
          color="neutral"
          variant="plain"
          sx={pillButtonSx}
        >
          Cancel
        </Button>
        <Button
          sx={{ ...pillButtonSx, px: 3 }}
          endDecorator={
            !pro ? (
              <Typography
                sx={{ color: "white" }}
                variant="outlined"
                color="neutral"
                level="body5"
              >
                PRO
              </Typography>
            ) : undefined
          }
          onClick={save}
          color="info"
        >
          {contractorIDs?.id ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </DialogClose>
  );
}
